import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import NoRecords from '~/components/General/NoRecords';
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import { DownloadFromTable, DownloadFromTableHover, NoRecordsDownloadIcon } from '~/assets/images';
import { displayTooltip } from '~/hooks/displayTooltip';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';
import { downloadFile } from '~/utils/helperFunctions';

import './style.scss';

const MAX_INTRINSIC_ROWS = 7;

const ExportLogsTable = ({ pagination, updatePagination, initialLoad }) => {
  const {
    exportLogs: rows = [],
    to,
    from,
    lastPage = 1,
    currentPage = 1,
    loading: isLoading,
    rowsCount = 0,
  } = useSelector((state) => state.exportLogsReducers);
  const perPage = useSelector((state) => state.exportLogsReducers.perPage) || DEFAULT_PAGE_SIZES[0];
  const isTooltip = displayTooltip();
  const [showNoRows, setShowNoRows] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const showLoader = initialLoad || isLoading || paginationLoading;
  const useIntrinsicTableHeight =
    !showLoader && rows.length > 0 && rows.length <= MAX_INTRINSIC_ROWS;

  const [downloading, setDownloading] = useState({
    status: false,
    logId: null,
  });

  // Reset pagination loading when new data arrives
  React.useEffect(() => {
    setPaginationLoading(false);
  }, [rows]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      setPaginationLoading(true);
      updatePagination({ page: newPage, pageSize: pagination.pageSize });
    },
    [pagination.pageSize, updatePagination]
  );

  const handleChangePageSize = useCallback(
    (event) => {
      setPaginationLoading(true);
      updatePagination({ page: 1, pageSize: event.target.value });
    },
    [updatePagination]
  );

  const downloadCSV = async (csvFileUrl, csvFileName, logId) => {
    setDownloading({
      status: true,
      logId: logId,
    });

    await downloadFile(csvFileUrl, csvFileName);

    setDownloading({
      status: false,
      logId: null,
    });
  };

  const formatModuleName = (moduleName) =>
    moduleName
      ?.replace(/([A-Z])/g, ' $1')
      ?.replace(/^./, (str) => str.toUpperCase())
      ?.trim();

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Exported Date',
      flex: 1,
      minWidth: 150,
      compactShrinkMinWidth: 110,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="tempValueWrapper" sx={{ marginLeft: '24px' }}>
          <Typography>{dateFormat(params.row.createdAt, true)}</Typography>
        </Box>
      ),
    },
    {
      field: 'module',
      headerName: 'Type',
      flex: 1,
      minWidth: 150,
      compactShrinkMinWidth: 100,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => <Typography>{formatModuleName(params.row.module)}</Typography>,
    },
    {
      field: 'creator',
      headerName: 'Exported By',
      flex: 1,
      minWidth: 150,
      compactShrinkMinWidth: 110,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => <Typography>{params.row?.creator?.fullName}</Typography>,
    },
    {
      field: 'download',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.fileName} disableHoverListener={isTooltip} disableInteractive>
          <Box
            className="addressWrapper"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              width: '100%',
            }}
            onClick={() => downloadCSV(params.row.fileUrl, params.row.fileName, params.row.id)}
          >
            {downloading.status && downloading.logId === params.row?.id ? (
              <CircularProgress
                sx={{
                  color: '#ed5c2f',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              <Box className="downloadIconWrapper">
                <img src={DownloadFromTable} alt="Download" className="downloadTableIcon" />
                <img
                  src={DownloadFromTableHover}
                  alt="Download"
                  className="downloadTableIconHover"
                />
              </Box>
            )}
          </Box>
        </Tooltip>
      ),
    },
  ];

  // Add delay for showing no rows overlay
  React.useEffect(() => {
    let timeoutId;

    if (!(initialLoad || isLoading || paginationLoading) && rows.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialLoad, isLoading, paginationLoading, rows.length]);

  if (rows?.length === 0 && !showLoader) {
    return (
      <Box className="importExportEmptyState">
        <NoRecords
          icon={<img src={NoRecordsDownloadIcon} alt="no-records" />}
          title={MESSAGES?.ACCOUNT?.EXPORT_LOGS?.NO_RECORDS_TITLE || 'No Exports Yet'}
          message={
            MESSAGES?.ACCOUNT?.EXPORT_LOGS?.NO_RECORDS_MESSAGE ||
            'Exports allow you to download your contacts or orders into a file you can save, share, or analyze outside the platform'
          }
          linkText="Learn more"
          linkUrl="https://help.openletterconnect.com/olc-help-docs/getting-started/setting-up-your-account/exporting-contacts"
          minBoxHeight="307px"
        />
      </Box>
    );
  }

  return (
    <Box
      className={`olcTableGridInternal lgTableHorizontalScroll ${initialLoad || isLoading || paginationLoading ? 'table-loading' : ''} ${rows.length === 0 ? 'table-empty' : ''}`}
    >
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight={useIntrinsicTableHeight}
          columnHeaderHeight={47}
          getRowId={(row) => row.id}
          disableColumnMenu
          disableRowSelectionOnClick
          pagination={false}
          rowHeight={61}
          shrinkColumnsToFit
          loading={initialLoad || isLoading || paginationLoading}
          sx={{
            ...(showNoRows ||
            (!(initialLoad || isLoading || paginationLoading) && rows.length === 0)
              ? {
                  height: 'auto',
                  minHeight: '200px',
                }
              : {}),
          }}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
          }}
        />
      </Box>

      {(showLoader || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={DEFAULT_PAGE_SIZES}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
          from={from}
          to={to}
          total={rowsCount}
          loading={showLoader}
        />
      )}
    </Box>
  );
};

export default ExportLogsTable;
